"use client";

import {
  evaluateCondition,
  getAnswer,
  DurationValueSchema,
  MoneyValueSchema,
  visibleFields,
  visibleSteps,
  type QuestionnaireAnswers,
  type QuestionnaireDefinition,
  type QuestionnaireField,
  type QuestionnaireStep,
} from "@repo/questionnaire";

import {
  FlowReviewCategory,
  FlowReviewContainer,
  FlowReviewItem,
} from "@/components/flow/flow-review";

const SKIP_REVIEW_STEP_IDS = new Set(["complete", "core-disclosure"]);

function formatFieldValue(
  field: QuestionnaireField,
  value: unknown,
): string | null {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (field.kind === "sensitive_identifier" && typeof value === "string") {
    if (field.identifierType === "ssn") {
      const digits = value.replace(/\D/g, "");
      if (digits.length === 9) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      }
    }
    return value;
  }
  if (field.kind === "duration") {
    const parsed = DurationValueSchema.safeParse(value);
    if (!parsed.success) return null;
    const unit =
      parsed.data.amount === 1
        ? parsed.data.unit.slice(0, -1)
        : parsed.data.unit;
    return `${parsed.data.amount} ${unit}`;
  }
  if (field.kind === "money") {
    const parsed = MoneyValueSchema.safeParse(value);
    if (!parsed.success) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: parsed.data.currency,
      maximumFractionDigits: 0,
    }).format(parsed.data.amountMinor / 100);
  }
  if (field.kind === "enum" || field.kind === "multi_select") {
    const options = field.options ?? [];
    if (Array.isArray(value)) {
      return value
        .map(
          (id) =>
            options.find((option) => option.id === id)?.label ?? String(id),
        )
        .join(", ");
    }
    if (typeof value === "string") {
      return options.find((option) => option.id === value)?.label ?? value;
    }
  }
  if (field.kind === "repeatable_group" && Array.isArray(value)) {
    if (value.length === 0) return null;
    return value
      .map((item, index) => {
        if (!item || typeof item !== "object") return `Entry ${index + 1}`;
        const parts = field.fields
          .map((child) => {
            const childValue = getAnswer(
              item as QuestionnaireAnswers,
              child.binding,
            );
            const formatted = formatFieldValue(child, childValue);
            return formatted ? `${child.label}: ${formatted}` : null;
          })
          .filter(Boolean);
        return parts.length > 0 ? parts.join(" · ") : `Entry ${index + 1}`;
      })
      .join("; ");
  }
  if (field.kind === "consent") return value === true ? "Agreed" : null;
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return null;
}

function reviewableSteps(
  definition: QuestionnaireDefinition,
  answers: QuestionnaireAnswers,
): QuestionnaireStep[] {
  return visibleSteps(definition, answers).filter((step) => {
    if (SKIP_REVIEW_STEP_IDS.has(step.id)) return false;
    const fields = visibleFields(step, answers).filter(
      (field) =>
        field.kind !== "computed_display" &&
        evaluateCondition(field.visibleWhen, answers),
    );
    return fields.some((field) => {
      const value = getAnswer(answers, field.binding);
      return formatFieldValue(field, value) != null;
    });
  });
}

export function QuestionnaireReview({
  definition,
  answers,
  onEdit,
  editLabel = "Edit",
}: {
  definition: QuestionnaireDefinition;
  answers: QuestionnaireAnswers;
  onEdit?: (stepId: string) => void;
  editLabel?: string;
}) {
  const sections = reviewableSteps(definition, answers);

  if (sections.length === 0) {
    return (
      <p className="text-muted-foreground">No answers to review yet.</p>
    );
  }

  return (
    <FlowReviewContainer>
      {sections.map((step) => {
        const rows = visibleFields(step, answers)
          .filter((field) => field.kind !== "computed_display")
          .flatMap((field) => {
            const formatted = formatFieldValue(
              field,
              getAnswer(answers, field.binding),
            );
            if (!formatted) return [];
            return [{ label: field.label, value: formatted }];
          });
        if (rows.length === 0) return null;
        return (
          <FlowReviewCategory
            key={step.id}
            title={step.title}
            editLabel={editLabel}
            onEdit={onEdit ? () => onEdit(step.id) : undefined}
          >
            <div className="grid grid-cols-2 gap-4">
              {rows.map((row) => (
                <FlowReviewItem
                  key={`${step.id}:${row.label}`}
                  label={row.label}
                  value={row.value}
                />
              ))}
            </div>
          </FlowReviewCategory>
        );
      })}
    </FlowReviewContainer>
  );
}

export function isQuestionnaireReviewStep(step: QuestionnaireStep): boolean {
  const fields = step.fields.filter((field) =>
    evaluateCondition(field.visibleWhen, {}),
  );
  if (fields.length === 0) return step.id === "complete";
  return fields.every(
    (field) =>
      field.kind === "computed_display" &&
      (field.calculationKey === "prequalification.review" ||
        field.calculationKey === "prequalification.complete"),
  );
}
